import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  FileText, 
  Upload,
  Trash2,
  ArrowLeft,
  FolderOpen,
  Calendar,
  Loader2,
  Home,
  Building,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const LGA_LIST = [
  "Brisbane City Council",
  "Moreton Bay Regional Council", 
  "Logan City Council",
  "Ipswich City Council",
  "Redland Bay City Council"
];

const PROPERTY_TYPE_LIST = [
  "Houses",
  "Units/Townhouses"
];

export default function ResearchManagement() {
  const [documents, setDocuments] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedLGA, setSelectedLGA] = useState("");
  const [selectedPropertyType, setSelectedPropertyType] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedLGAs, setExpandedLGAs] = useState({});
  const [expandedPropertyTypes, setExpandedPropertyTypes] = useState({});
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get("/research/documents");
      setDocuments(response.data.documents);
      
      // Expand all LGAs by default in admin view
      const expanded = {};
      Object.keys(response.data.documents).forEach(lga => {
        expanded[lga] = true;
      });
      setExpandedLGAs(expanded);
    } catch (error) {
      console.error("Error fetching documents:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLGA = (lga) => {
    setExpandedLGAs(prev => ({ ...prev, [lga]: !prev[lga] }));
  };

  const togglePropertyType = (lga, pt) => {
    const key = `${lga}-${pt}`;
    setExpandedPropertyTypes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error("Only PDF files are allowed");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedLGA) {
      toast.error("Please select a council area");
      return;
    }
    if (!selectedPropertyType) {
      toast.error("Please select a property type");
      return;
    }
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("lga", selectedLGA);
      formData.append("property_type", selectedPropertyType);
      formData.append("file", selectedFile);
      
      await api.post("/admin/research/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Document uploaded successfully");
      setSelectedFile(null);
      setSelectedLGA("");
      setSelectedPropertyType("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (documentId, filename) => {
    if (!confirm(`Are you sure you want to delete "${filename}"?`)) return;
    
    try {
      await api.delete(`/admin/research/${documentId}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getTotalDocuments = () => {
    let total = 0;
    Object.values(documents).forEach(lgaData => {
      Object.values(lgaData).forEach(docs => {
        total += docs.length;
      });
    });
    return total;
  };

  const getLGADocumentCount = (lgaData) => {
    return Object.values(lgaData).reduce((sum, docs) => sum + docs.length, 0);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="research-management-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate("/admin")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">
            Research Document Management
          </h2>
          <p className="text-slate-500">
            Upload and manage suburb research documents
          </p>
        </div>
      </div>

      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload New Document
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Council Area *</Label>
              <Select value={selectedLGA} onValueChange={setSelectedLGA}>
                <SelectTrigger data-testid="lga-select">
                  <SelectValue placeholder="Select council" />
                </SelectTrigger>
                <SelectContent>
                  {LGA_LIST.map(lga => (
                    <SelectItem key={lga} value={lga}>{lga}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Property Type *</Label>
              <Select value={selectedPropertyType} onValueChange={setSelectedPropertyType}>
                <SelectTrigger data-testid="property-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPE_LIST.map(pt => (
                    <SelectItem key={pt} value={pt}>
                      <div className="flex items-center gap-2">
                        {pt === "Houses" ? <Home className="w-4 h-4" /> : <Building className="w-4 h-4" />}
                        {pt}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>PDF File *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#547d91] file:text-white hover:file:bg-[#456a7a] cursor-pointer"
                data-testid="file-input"
              />
              {selectedFile && (
                <p className="text-xs text-slate-500 truncate">
                  Selected: {selectedFile.name}
                </p>
              )}
            </div>
            
            <div className="flex items-end">
              <Button
                onClick={handleUpload}
                disabled={isUploading || !selectedLGA || !selectedPropertyType || !selectedFile}
                className="w-full bg-[#547d91] hover:bg-[#456a7a]"
                data-testid="upload-btn"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Documents by Council Area ({getTotalDocuments()} total)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(documents).map(([lga, lgaData]) => {
            const lgaDocCount = getLGADocumentCount(lgaData);
            
            return (
              <div key={lga} className="border rounded-lg overflow-hidden">
                {/* LGA Header */}
                <button
                  onClick={() => toggleLGA(lga)}
                  className="w-full p-3 flex items-center justify-between hover:bg-slate-50 transition-colors bg-slate-50"
                >
                  <div className="flex items-center gap-3">
                    <FolderOpen className={cn(
                      "w-5 h-5",
                      lgaDocCount > 0 ? "text-[#547d91]" : "text-slate-400"
                    )} />
                    <span className="font-medium">{lga}</span>
                    <span className="text-sm text-slate-500">
                      ({lgaDocCount} {lgaDocCount === 1 ? "document" : "documents"})
                    </span>
                  </div>
                  {expandedLGAs[lga] ? (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                </button>
                
                {/* Property Type Sub-folders */}
                {expandedLGAs[lga] && (
                  <div className="border-t">
                    {PROPERTY_TYPE_LIST.map(propertyType => {
                      const docs = lgaData[propertyType] || [];
                      const ptKey = `${lga}-${propertyType}`;
                      
                      return (
                        <div key={propertyType} className="border-b last:border-b-0">
                          {/* Property Type Header */}
                          <button
                            onClick={() => togglePropertyType(lga, propertyType)}
                            className="w-full pl-8 pr-3 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              {propertyType === "Houses" ? (
                                <Home className="w-4 h-4 text-blue-500" />
                              ) : (
                                <Building className="w-4 h-4 text-purple-500" />
                              )}
                              <span className="font-medium text-sm">{propertyType}</span>
                              <span className="text-xs text-slate-500">
                                ({docs.length})
                              </span>
                            </div>
                            {expandedPropertyTypes[ptKey] ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                          
                          {/* Documents */}
                          {expandedPropertyTypes[ptKey] && (
                            <div className="bg-white">
                              {docs.length === 0 ? (
                                <p className="pl-16 py-3 text-sm text-slate-400">
                                  No documents uploaded
                                </p>
                              ) : (
                                docs.map(doc => (
                                  <div 
                                    key={doc.document_id}
                                    className="pl-16 pr-4 py-2 flex items-center justify-between hover:bg-slate-50 border-t border-slate-100"
                                  >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">
                                          {doc.original_filename}
                                        </p>
                                        <div className="flex items-center gap-2 text-xs text-slate-500">
                                          <span>{formatDate(doc.uploaded_at)}</span>
                                          <span>•</span>
                                          <span>{formatFileSize(doc.file_size)}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDelete(doc.document_id, doc.original_filename)}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`delete-${doc.document_id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
